<template>
  <div :class="['main-window', hideGuide ? 'main-window__no-guide' : 'main-window__guide']">
    <div class="user-management">
      <transition name="fade">
        <simple-feedback-modal
          v-if="showFeedbackModal"
          @feedbackSent="userFeedbackSubmitted"
          @close="toggleFeedbackModal"
        />
      </transition>
      <transition name="fade">
        <user-modal
          v-if="showAddUserModal"
          :mode="'add'"
          :loading="loading"
          @submit="createUser"
          @close="toggleAddUserModal"
        />
      </transition>
      <transition name="fade">
        <user-modal
          v-if="showEditUserModal"
          :user="selectedUser"
          :mode="'edit'"
          :loading="loading"
          @delete="promptDeleteUser"
          @submit="updateUser"
          @close="toggleEditUserModal"
        />
      </transition>
      <transition name="fade">
        <cards-modal
          v-if="showCardsModal"
          :user="selectedUser"
          :loading="loading"
          @toggle-step-up="updateBypassCards"
          @add-card="addCard"
          @close="toggleCardsModal"
        />
      </transition>
      <transition name="fade">
        <credential-management-modal
          v-if="showCredentialsModal"
          :user="selectedUser"
          @close="toggleCredentialsModal"
          @update-prop="updateSelectedUser"
        />
      </transition>
      <!-- Keep at the bottom to ensure they render above all other modals -->
      <transition name="fade">
        <confirmation-modal
          v-if="showEnrollmentConfirmation"
          :confirmation-type="bcMobileConfirmation.type"
          :title="bcMobileConfirmation.title"
          :description="bcMobileConfirmation.description"
          :button-text="bcMobileConfirmation.button"
          @confirm="enrollMobile"
          @cancel="toggleEnrollmentConfirmation"
        >
        </confirmation-modal>
      </transition>
      <transition name="fade">
        <confirmation-modal
          v-if="showDeleteUserConfirmation"
          :confirmation-type="deleteUserConfirmation.type"
          :title="deleteUserConfirmation.title"
          :description="deleteUserConfirmation.description"
          :button-text="deleteUserConfirmation.button"
          @confirm="confirmDeleteUser"
          @cancel="cancelDeleteUser"
        >
        </confirmation-modal>
      </transition>
      <div class="page-topbar mb-2">
        <div class="page-topbar__title">
          User Management
          <template v-if="lastSyncEvent">
            <span
              id="last-sync-date"
              class="page-topbar__subtitle"
            >
              {{ lastSyncEvent.message }} {{ lastSyncEvent.date | date }}
            </span>
          </template>
        </div>
        <button
          id="add-user-button"
          class="btn btn--raised"
          @click="toggleAddUserModal"
        >
          Add User
        </button>
      </div>
      <user-table
        :loading="loading"
        :rows="userTableData"
        :total-records="totalUserRecords"
        :is-modal-open="showCardsModal || showCredentialsModal"
        :user-pagination-params.sync="paginationParams"
        :search.sync="searchTerm"
        :active-authenticator="activeAuthenticator"
        :credentials="credentials"
        @enroll-mobile="toggleEnrollmentConfirmation"
        @toggle-user="toggleAllStepUp"
        @manage-cards="toggleCardsModal"
        @manage-credentials="toggleCredentialsModal"
        @send-stepup="sendVerification"
        @edit-user="toggleEditUserModal"
      />
    </div>
    <div class="help-banner">
      <div class="help-banner__item">
        How to Add:
        <a
          class="help-banner__link"
          target="_blank"
          href="https://bit.ly/3SMqnvA"
        > Card </a>
        |
        <a
          class="help-banner__link"
          target="_blank"
          href="https://bit.ly/47D72Rr"
        > User ID </a>
        |
        <a
          class="help-banner__link"
          target="_blank"
          href="https://bit.ly/47itekc"
        > PIN </a>
        |
        <a
          class="help-banner__link"
          target="_blank"
          href="https://bit.ly/49EtCel"
        > Fingerprint </a>
      </div>
    </div>
  </div>
</template>

<script>
import userQueries from '@/graphql/users.js'
import cardQueries from '@/graphql/cards.js'
import syncEventQueries from '@/graphql/syncEvents.js'
import transactionQueries from '@/graphql/transactions.js'
import settingQueries from '@/graphql/settings.js'
import credentialQueries from '@/graphql/credentials.js'
import UserTable from '@/components/userTable.vue'
import UserModal from '@/components/userModal.vue'
import CardsModal from '@/components/manageCards.vue'
import ConfirmationModal from '@/components/confirmationModal.vue'
import SimpleFeedbackModal from '@/components/simpleFeedbackModal.vue'
import CredentialManagementModal from '@/components/credentialManagementModal.vue'

export default {
  name: 'UserManagement',
  components: {
    UserTable,
    UserModal,
    CardsModal,
    ConfirmationModal,
    SimpleFeedbackModal,
    CredentialManagementModal,
  },
  mixins: [
    userQueries,
    cardQueries,
    syncEventQueries,
    transactionQueries,
    settingQueries,
    credentialQueries,
  ],
  data() {
    return {
      loading: false,
      showFeedbackModal: false,
      showAddUserModal: false,
      showEditUserModal: false,
      showCardsModal: false,
      showCredentialsModal: false,
      showEnrollmentConfirmation: false,
      showDeleteUserConfirmation: false,
      activeAuthenticator: null,
      lastSyncEvent: null,
      selectedUser: null,
      credentials: [],
      userTableData: [],
      selectedUsers: [],
      searchTerm: '',
      totalUserRecords: 0,
      paginationParams: {
        page: 1,
        perPage: 10,
      },
      hideGuide: JSON.parse(localStorage.getItem('hideGuide', false)),
      bcMobileConfirmation: {
        type: 'friendly',
        title: 'Activate BioConnect Mobile',
        description:
          'An activation email will be sent to each selected user, inviting them to download and activate the BioConnect Mobile authentication app.',
        button: 'Send Invitation',
      },
      usernameToDelete: null,
      deleteUserConfirmation: {
        type: 'danger',
        title: 'Delete User',
        button: 'Delete User'
      }
    }
  },
  watch: {
    searchTerm() {
      this.setSearch()
    },
    paginationParams: {
      handler: function () {
        this.fetchUsers()
      },
      deep: true,
    },
    showModal() {
      // Only show 1 modal at a time
      if (this.showAddUserModal) {
        (this.showCardsModal = false), (this.showCredentialsModal = false)
      } else if (this.showCardsModal) {
        (this.showAddUserModal = false), (this.showCredentialsModal = false)
      } else if (this.showCredentialsModal) {
        (this.showAddUserModal = false), (this.showCardsModal = false)
      }
    },
  },
  created() {
    this.fetchUsers()
    this.fetchAccountSetting()
    this.fetchLastSync()
    this.fetchCredentials()

    // Handle closing the welcome banner
    window.addEventListener('hideGuide', () => {
      this.hideGuide = true
    })
  },
  methods: {
    async fetchCredentials() {
      const _credentialParams = {
        page: 1,
        perPage: 100,
      }
      try {
        const {credentialsList} = await this.listCredentialsQuery(_credentialParams)
        this.credentials = credentialsList
      } catch (error) {
        console.log(error)
      }
    },
    async fetchAccountSetting() {
      try {
        const {authenticator} = await this.listSettingsQuery()
        this.activeAuthenticator = authenticator
      } catch (error) {
        console.log(error)
      }
    },
    async fetchLastSync() {
      const _syncParams = {
        page: 1,
        perPage: 10,
      }
      try {
        const _syncEvents = await this.listSyncEventsQuery(_syncParams)
        if (_syncEvents.length > 0) {
          this.getFirstSyncEvent(_syncEvents)
        }
      } catch (error) {
        this.displayAlert('fail', 'Unable to retrieve sync events.')
        console.log(error)
      }
    },
    /*
     * Async call to fetch the record for the given user
     */
    async fetchUser(user) {
      this.loading = true
      try {
        const result = await this.listUsersQuery({page: 1, perPage: 10}, user.username, false)
        this.loading = false
        const {userList} = result
        return userList[0]
      } catch (error) {
        this.loading = false
        this.displayAlert('fail', 'Unable to load user')
      }
    },
    async fetchUsers() {
      this.loading = true
      try {
        const result = await this.listUsersQuery(this.paginationParams, this.searchTerm, false)
        const {userList, totalRows} = result
        this.totalUserRecords = totalRows
        this.addToTable(userList)
        this.loading = false
      } catch (error) {
        this.loading = false
        this.displayAlert('fail', 'Unable to retrieve users.')
        console.log('Fetch Users Failed.', error)
      }
    },
    async sendVerification(usernames) {
      this.loading = true
      await Promise.all(
        usernames.map(async (username) => {
          return await this.createTransaction(username)
        }),
      )
        .then((transactions) => {
          this.loading = false
          transactions.forEach((transaction) => {
            if (transaction.isValid) {
              this.displayAlert('success', `${transaction.username} verified!`)
            } else {
              this.displayAlert('fail', `${transaction.username} declined verification!`)
            }
          })
        })
        .catch((error) => {
          this.loading = false
          this.handleTransactionError(error)
          console.log(error)
        })
    },
    async enrollMobile() {
      this.loading = true
      this.showEnrollmentConfirmation = false
      await Promise.all(
        this.selectedUsers.map(async (user) => {
          return this.enrollUserAuthentication(user.username)
        }),
      )
        .then((results) => {
          this.loading = false
          this.showEnrollmentConfirmation = false
          this.displayAlert('success', 'Invite email sent to selected users.')
        })
        .catch((error) => {
          this.loading = false
          this.displayAlert('fail', 'Unable to send email to selected users.')
          console.log(error)
        })
    },
    async createUser(userInfo) {
      this.loading = true
      try {
        const user = await this.createUserMutation(userInfo)
        this.loading = false
        this.displayAlert('success', 'Created User.')
        this.fetchUsers()
        this.toggleAddUserModal()
        if (userInfo.addCard) {
          const _user = {
            originalIndex: this.userTableData.length,
            ...user,
          }
          this.toggleCardsModal([_user])
        } else if (this.isUserFeedbackNotSubmitted()) {
          this.toggleFeedbackModal()
        }
      } catch (error) {
        this.loading = false
        console.log('Unable to create user.', error)
        this.handleCreateError(error)
      }
    },
    async updateUser(userDetails) {
      this.loading = true
      try {
        await this.updateUserMutation(userDetails)
        this.loading = false
        this.fetchUsers()
        this.toggleEditUserModal()
        this.displayAlert('success', 'Updated user.')
      } catch (error) {
        this.loading = false
        this.displayAlert('fail', 'Unable to update user.')
        console.log(error)
      }
    },
    async updateSelectedUser() {
      // No user selected
      if (!this.selectedUser) {
        return
      }

      const updatedUser = await this.fetchUser(this.selectedUser)
      const userInd = this.userTableData.findIndex((user) => {
        return user.username === updatedUser.username
      })

      if (userInd < 0) {
        return
      }

      this.userTableData.splice(userInd, 1, updatedUser)
      this.selectedUser = updatedUser
    },
    promptDeleteUser(username) {
      this.usernameToDelete = username
      this.deleteUserConfirmation.description = `Are you sure you want to delete ${username}?`
      this.showCredentialsModal = false
      this.showDeleteUserConfirmation = true
    },
    async confirmDeleteUser() {
      // Only close the edit user modal once the delete is confirmed
      this.showDeleteUserConfirmation = false
      this.showEditUserModal = false
      await this.removeUser(this.usernameToDelete)
    },
    cancelDeleteUser() {
      this.showDeleteUserConfirmation = false
    },
    async removeUser(username) {
      this.loading = true
      try {
        await this.deleteUserMutation(username)
        this.loading = false
        this.displayAlert('success', `User deleted.`)
        this.fetchUsers()
      } catch(error) {
        console.log('Unable to delete user.', error)
        this.loading = false
        this.displayAlert('fail', 'Unable to delete user.')
      }
    },
    async addCard(card) {
      this.loading = true
      try {
        await this.updateCardMutation(card)
        this.loading = false
        this.fetchUsers()
        this.displayAlert('success', `Added credential to ${this.selectedUser.username}.`)
        this.toggleCardsModal()
        if (this.isUserFeedbackNotSubmitted()) {
          this.toggleFeedbackModal()
        }
      } catch (error) {
        this.loading = false
        this.displayAlert('fail', 'Unable to add credential.')
      }
    },
    errorType(message) {
      if (message.includes('phone number')) {
        return 'phone'
      } else if (message.includes('username')) {
        return 'username'
      } else if (message.includes('authenticator setup')) {
        return 'authenticator'
      }
      return 'general'
    },
    handleTransactionError(error) {
      const _errorType = this.errorType(error.message)
      const _errorMessages = {
        username: 'Authenticator username may not be valid.',
        phone: 'User does not have a phone number.',
        authenticator: 'Please set an authenticator providor.',
        general: 'Unable to process authentication request.',
      }
      this.displayAlert('fail', _errorMessages[_errorType])
    },
    handleCreateError(error) {
      if (error.message.includes('user exists')) {
        this.displayAlert('fail', 'User already exists.')
      } else {
        this.displayAlert('fail', 'Unable to create user.')
      }
    },
    setSearch() {
      this.paginationParams = {
        ...this.paginationParams,
        page: 1,
      }
    },
    /*
     * During ACM sync, if the sync didnt find a third party id for a user we
     * then put the email value in for username, if this happens we should
     * strip out the acm sync details, and show these fields were not available
     * for the user. (ie: ExternalId@ExternalId.ACMSyncService.com)
     */
    addToTable(userList) {
      this.userTableData = userList.filter((user) => {
        return user.username !== 'bypassUser'
      })
    },
    /*
     * Retreive only the first event because this is the latest event.
     * If an endtime is available, the sync was successful
     * Otherwise show the date when the sync started
     */
    getFirstSyncEvent([syncEvent]) {
      const _message = 'Last sync'
      if (syncEvent.endTime) {
        this.lastSyncEvent = {
          message: `${_message} completed on`,
          date: syncEvent.endTime,
        }
      } else if (syncEvent.startTime) {
        this.lastSyncEvent = {
          message: `${_message} started on`,
          date: syncEvent.startTime,
        }
      } else {
        this.lastSyncEvent = null
      }
    },
    toggleAllStepUp(selectedUsers) {
      const {users, isBypass} = selectedUsers
      this.loading = true
      users.forEach((user) => {
        const {cards} = user
        if (cards.length > 0) {
          const _payload = {
            user,
            isBypass,
            cards,
          }
          this.updateBypassCards(_payload)
        } else {
          this.loading = false
          this.displayAlert('fail', 'User has no cards.')
        }
      })
    },
    async updateBypassCards(userCards) {
      const {user, cards, isBypass} = userCards
      this.loading = true
      await Promise.all(
        cards.map(async (card) => {
          const _data = {
            username: user.username,
            uuid: card.uuid,
            isBypass: isBypass,
          }
          return await this.updateCardMutation(_data)
        }),
      )
        .then(() => {
          this.loading = false
          this.displayAlert(
            'success',
            `${isBypass ? 'Disabled' : 'Enabled'} two-factor for ${user.username}'s credential.`,
          )
          this.fetchUsers()
        })
        .catch((error) => {
          this.loading = false
          this.displayAlert(
            'fail',
            `Unable to ${isBypass ? 'disable' : 'enable'} two-factor for ${user.username}'s credential.`,
          )
          console.log(error)
        })
    },
    isUserFeedbackNotSubmitted() {
      return localStorage.getItem('userFeedbackSubmitted') === null
    },
    userFeedbackSubmitted() {
      localStorage.setItem('userFeedbackSubmitted', true)
    },
    toggleFeedbackModal() {
      this.showFeedbackModal = !this.showFeedbackModal
    },
    toggleAddUserModal() {
      this.showAddUserModal = !this.showAddUserModal
    },
    toggleCardsModal(user) {
      this.selectedUser = user ? user[0] : null
      this.showCardsModal = !this.showCardsModal
    },
    toggleCredentialsModal(user) {
      this.selectedUser = user ? user[0] : null
      this.showCredentialsModal = !this.showCredentialsModal
    },
    updatePaginationParams(params) {
      const {pagination, searchTerm} = params
      this.searchTerm = searchTerm
      this.paginationParams = pagination
    },
    toggleEditUserModal(user) {
      this.selectedUser = user ? user[0] : null
      this.showEditUserModal = !this.showEditUserModal
    },
    toggleEnrollmentConfirmation(selectedUsers) {
      if (selectedUsers) {
        this.selectedUsers = selectedUsers
      }
      this.showEnrollmentConfirmation = !this.showEnrollmentConfirmation
    },
  },
}
</script>

<style lang="scss" scoped>
  .user-management {
    flex: 1;
  }
</style>
